import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Spinner,
  Badge,
  useToast,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Tooltip,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { quickbooksService } from "../../api/quickbooks";

const QuickBooksRawData = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 0,
  });

  const fetchRawData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await quickbooksService.getRawTransactions(customerId, { page });
      
      if (response.data) {
        setRawData(response.data.results || []);
        setPagination({
          count: response.data.count || 0,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: page,
          totalPages: Math.ceil((response.data.count || 0) / 50),
        });
      }
    } catch (error) {
      console.error('Error fetching raw data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch raw transaction data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, toast]);

  const fetchCustomerInfo = useCallback(async () => {
    try {
      const response = await quickbooksService.getCustomerById(customerId);
      if (response.data) {
        setCustomerName(response.data.name || 'Unknown Customer');
      }
    } catch (error) {
      console.error('Error fetching customer info:', error);
      setCustomerName('Unknown Customer');
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) {
      fetchCustomerInfo();
      fetchRawData(1);
    }
  }, [customerId, fetchCustomerInfo, fetchRawData]);

  const handlePageChange = (page) => {
    fetchRawData(page);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(amount));
  };

  if (loading && rawData.length === 0) {
    return (
      <Box maxW="100%" pt={8} px={4}>
        <Flex direction="column" align="center" justify="center" minH="400px">
          <Spinner size="xl" />
          <Text mt={4} color="gray.600">Loading raw transaction data...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box maxW="100%" py={8} px={4}>
      <Card>
        <CardHeader>
          <Flex justify="space-between" align="center" direction={{ base: 'column', md: 'row' }} gap={4}>
            <Box>
              <Button
                leftIcon={<ArrowBackIcon />}
                variant="ghost"
                onClick={() => navigate('/account-management')}
                mb={2}
                size="sm"
              >
                Back to Account Management
              </Button>
              <Heading size="lg">QuickBooks Raw Data</Heading>
              <Text mt={2} color="gray.600">
                Raw transaction data for: <strong>{customerName}</strong>
              </Text>
            </Box>
            
            <Flex direction={{ base: 'column', lg: 'row' }} gap={4} alignItems={{ base: 'stretch', lg: 'center' }}>
              <HStack spacing={3} flex={{ lg: '1' }} justifyContent={{ lg: 'flex-end' }}>
                <StatGroup>
                  <Stat size="sm">
                    <StatLabel>Total Records</StatLabel>
                    <StatNumber>{pagination.count.toLocaleString()}</StatNumber>
                  </Stat>
                  <Stat size="sm">
                    <StatLabel>Current Page</StatLabel>
                    <StatNumber>{pagination.currentPage} of {pagination.totalPages}</StatNumber>
                  </Stat>
                </StatGroup>
              </HStack>
            </Flex>
          </Flex>
        </CardHeader>
        
        <CardBody>
          {rawData.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Text fontSize="lg" color="gray.600">
                No raw transaction data found for this customer.
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Raw data is only available after running a Full Sync import.
              </Text>
            </Box>
          ) : (
            <VStack spacing={6} align="stretch">
              <Box overflowX="auto">
                <Table variant="simple" size="sm" width="100%">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Date</Th>
                      <Th>Account</Th>
                      <Th>Name</Th>
                      <Th style={{width: "800px", maxWidth: 'min(800px, 50%)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>Memo</Th>
                      <Th>Amount</Th>
                      <Th>Split</Th>
                      <Th>Batch ID</Th>
                      <Th>Imported</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rawData.map((transaction) => (
                      <Tr key={transaction.id} _hover={{ bg: "gray.50" }}>
                        <Td><Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">{formatDate(transaction.date)}</Text></Td>
                        <Td>
                          <Tooltip label={transaction.account}><Text fontSize="sm" fontFamily="mono" cursor="help" whiteSpace="nowrap">{transaction.account}</Text></Tooltip>
                        </Td>
                        <Td>
                          <Tooltip label={transaction.description ?? transaction.name}><Text fontSize="sm" cursor="help" whiteSpace="nowrap">{transaction.description ?? transaction.name}</Text></Tooltip>
                        </Td>
                        <Td style={{width: "800px", maxWidth: 'min(800px, 50%)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          <Tooltip label={transaction.memo}>
                            <Text fontSize="sm" cursor="help" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{transaction.memo}</Text>
                          </Tooltip>
                        </Td>
                        <Td>
                          <Text fontSize="sm" fontWeight="medium" color={parseFloat(transaction.amount) >= 0 ? "green.600" : "red.600"} whiteSpace="nowrap">{formatAmount(transaction.amount)}</Text>
                        </Td>
                        <Td>
                          <Tooltip label={transaction.split}><Text fontSize="sm" cursor="help" whiteSpace="nowrap">{transaction.split}</Text></Tooltip>
                        </Td>
                        <Td>
                          <Badge variant="outline" fontSize="xs">{transaction.import_batch_id?.substring(0, 8)}...</Badge>
                        </Td>
                        <Td>
                          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{formatDateTime(transaction.created_on)}</Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
              
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <Flex justify="center" align="center" gap={4}>
                  <Button
                    leftIcon={<ChevronLeftIcon />}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    isDisabled={!pagination.previous || loading}
                    size="sm"
                  >
                    Previous
                  </Button>
                  
                  <HStack spacing={2}>
                    {/* Show page numbers */}
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }
                      
                      return (
                        <Button
                          key={pageNum}
                          size="sm"
                          variant={pageNum === pagination.currentPage ? "solid" : "outline"}
                          colorScheme={pageNum === pagination.currentPage ? "blue" : "gray"}
                          onClick={() => handlePageChange(pageNum)}
                          isLoading={loading}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </HStack>
                  
                  <Button
                    rightIcon={<ChevronRightIcon />}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    isDisabled={!pagination.next || loading}
                    size="sm"
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default QuickBooksRawData; 